import {useEffect, useMemo, useRef, useState} from 'react';
import {APP_BASE, Link} from './router';
import {useHermes} from './lib/store';
import {design2Conversation} from './design2-story';
import {DesignPreviewLanguages, usePublicLocale} from './public-locale';
import {commerceProduct, type CommerceProduct} from '../src/commerce-types';
import './design2.css';
import './design3.css';

const appearances: Record<string, {artwork: string; team: string; alt: string; description: string; services: string}> = {
  warm: {artwork: 'design2', team: 'team-peach', alt: 'Orange Operator, purple Head of Eight and blue R&D with a soft peach backdrop',
    description: 'Build, create and run your ideas with a team that remembers where you left off.', services: 'More from EightBots'},
  robots: {artwork: 'design3', team: 'team', alt: 'Charcoal Operator, blue Head of Eight and lime R&D robots',
    description: 'Tell your bots what you need.', services: 'Your bots handle setup.'},
};
const products: {id: CommerceProduct; image: string; description: string}[] = [
  {id: 'domains', image: 'domain', description: 'Your agents register domains and handle DNS and HTTPS.'},
  {id: 'payments', image: 'cards', description: 'Create a checkout for your apps and services.'},
  {id: 'crypto', image: 'crypto', description: 'Create crypto checkout links and track orders.'},
];

function TeamIllustration({artwork, team, alt}: {artwork: string; team: string; alt: string}) {
  const {t} = usePublicLocale();
  return <figure className="d2-team">
    <Link className="d2-whatsapp" href={`${APP_BASE}/settings?connect=whatsapp`} aria-label={t('Talk to your team on WhatsApp')}>
      <span>{t('We can also talk on')}</span>
      <span className="d2-whatsapp-brand"><img src={`${APP_BASE}/assets/whatsapp-official.svg`} width={26} height={26} alt=""/><strong>WhatsApp</strong></span>
    </Link>
    <picture>
      <source media="(max-width:383px)" srcSet={`${artwork}/${team}-280.webp`} width={280} height={108}/>
      <source media="(max-width:499px)" srcSet={`${artwork}/${team}-320.webp`} width={320} height={123}/>
      <source media="(max-width:679px)" srcSet={`${artwork}/${team}-420.webp`} width={420} height={161}/>
      <source media="(min-width:1021px) and (max-width:1279px)" srcSet={`${artwork}/${team}-460.webp`} width={460} height={177}/>
      <img src={`${artwork}/${team}-560.webp`} width={560} height={215} alt={t(alt)} fetchPriority="high"/>
    </picture>
    <figcaption><span>{t('Operator')}</span><span>{t('Head of Eight')}</span><span>{t('R&D')}</span></figcaption>
  </figure>;
}

function ExampleConversation({entryHref, artwork}: {entryHref: string; artwork: string}) {
  const {locale, t} = usePublicLocale();
  const {story, timeline} = useMemo(() => design2Conversation(locale), [locale]);
  const root = useRef<HTMLElement>(null);
  const [elapsed, setElapsed] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [visible, setVisible] = useState(false);
  const [foreground, setForeground] = useState(() => !document.hidden);
  let position = 0;
  while (position < timeline.length - 1 && elapsed >= timeline[position]!.end) position++;
  const current = story[position]!;
  const timing = timeline[position]!;
  const lastReply = timeline[timeline.length - 1]!.complete;
  const finished = position === story.length - 1 && (reducedMotion || elapsed >= timing.complete);
  const typing = !reducedMotion && current.customer !== null && elapsed < timing.sent;
  const silent = !reducedMotion && current.customer === null && elapsed < timing.reply;
  const thinking = !reducedMotion && current.customer !== null && elapsed >= timing.sent && elapsed < timing.reply;
  const responding = !reducedMotion && elapsed >= timing.reply && elapsed < timing.complete;
  const previous = story[position - 1];
  const customer = typing || silent ? previous?.customer : current.customer;
  const reply = typing || silent ? previous?.head : thinking ? t('Thinking…') : reducedMotion ? current.head : current.head.slice(0, Math.floor((elapsed - timing.reply) / (timing.complete - timing.reply) * current.head.length));
  const draft = typing && current.customer !== null ? current.customer.slice(0, Math.floor((elapsed - timing.start) / (timing.sent - timing.start) * current.customer.length)) : '';
  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const changeMotion = () => setReducedMotion(motion.matches);
    const changeVisibility = () => setForeground(!document.hidden);
    const observer = new IntersectionObserver(([entry]) => setVisible(Boolean(entry?.isIntersecting)), {threshold: .5});
    observer.observe(root.current!);
    motion.addEventListener('change', changeMotion);
    document.addEventListener('visibilitychange', changeVisibility);
    return () => {observer.disconnect(); motion.removeEventListener('change', changeMotion); document.removeEventListener('visibilitychange', changeVisibility);};
  }, []);
  useEffect(() => {
    if (reducedMotion || finished || !visible || !foreground) return;
    let previousTick = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      const delta = now - previousTick;
      previousTick = now;
      setElapsed(value => Math.min(lastReply, value + delta));
    }, 32);
    return () => window.clearInterval(timer);
  }, [lastReply, reducedMotion, finished, visible, foreground]);
  return <section className="d2-example" ref={root} lang={locale} dir="ltr" aria-labelledby="d2-example-title">
    <div className="d2-example-heading"><h2 id="d2-example-title">{t('Example conversation')}</h2></div>
    <div className="d2-chat-window" aria-hidden="true"><div className="d2-messages">
      {customer && <p className="d2-message d2-message-customer" key={customer}>{customer}</p>}
      {reply && <div className="d2-reply"><img src={`${artwork}/head-avatar-40.webp`} width={40} height={40} alt=""/>
        <p className={`d2-message d2-message-head${thinking ? ' d2-thinking' : ''}${responding ? ' d2-typing' : ''}`}>{reply}</p></div>}
    </div></div>
    <ol className="d2-sr-only">{story.map((exchange, index) => <li key={index}>
      {exchange.customer !== null && <p>{t('Customer')}: {exchange.customer}</p>}<p>{t('Head of Eight')}: {exchange.head}</p>
    </li>)}</ol>
    <Link className={`d2-composer${typing ? ' d2-composer-typing' : ''}`} href={entryHref} aria-label={t('Start your own conversation with EightBots')}>
      <span className="d2-composer-copy" aria-hidden="true"><span className={typing ? 'd2-typing' : ''}>{typing ? draft : t('Message Eightbots...')}</span></span><span className="d2-send" aria-hidden="true">↗</span>
    </Link>
  </section>;
}

export function Design2Home({design}: {design: string}) {
  const {state} = useHermes();
  const {locale, t} = usePublicLocale();
  const appearance = appearances[design];
  if (!appearance) throw new Error(`Unknown preview design: ${design}`);
  const artwork = `${APP_BASE}/assets/${appearance.artwork}`;
  const entryHref = state.session.data ? `${APP_BASE}/workspace` : `${APP_BASE}/register?next=%2Fworkspace`;
  return <div className={`design2-home design-${design}-home`}>
    <DesignPreviewLanguages design={design}/>
    <section className="d2-hero" aria-labelledby="d2-title">
      <div className="d2-intro">
        <h1 id="d2-title">{t('Your AI team.')}<br/>{t('Ready to work.')}</h1>
        <p className="d2-description">{t(appearance.description)}</p>
        <Link href={entryHref} className="d2-primary">{t('Get started')} <span aria-hidden="true">↗</span></Link>
        <p className="d2-supporting">{t('Work with eight agents in a private workspace.')}<br/>{t('Use your browser or WhatsApp.')}</p>
      </div>
      <div className="d2-demonstration"><TeamIllustration artwork={artwork} team={appearance.team} alt={appearance.alt}/><ExampleConversation key={`${design}-${locale}`} entryHref={entryHref} artwork={artwork}/></div>
    </section>
    <section className="d2-products" aria-labelledby="d2-products-title">
      <h2 id="d2-products-title">{t(appearance.services)}</h2>
      <div className="d2-product-list">{products.map(item => {
        const product = commerceProduct(item.id);
        return <article className="d2-product" key={product.id}>
          <h3>EightBots<br/>{product.name.replace(/^EightBots /, '')}</h3>
          <p>{t(item.description)}</p>
          <Link href={`${APP_BASE}${product.publicPath}`} aria-label={`${t(product.name)} — ${t('Explore')}`}>{t('Explore')} <span aria-hidden="true">→</span></Link>
          <picture><source media="(max-width:499px), (min-width:1021px) and (max-width:1279px)" srcSet={`${artwork}/${item.image}-48.webp`} width={48} height={38}/>
            <img src={`${artwork}/${item.image}-65.webp`} width={65} height={51} alt="" loading="lazy"/></picture>
        </article>;
      })}</div>
    </section>
  </div>;
}
